import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { login } from "@/auth";

const ACCENT = "#7f5af0";
const DARK = "#0a0a0f";
const CARD_BG = "#13131a";
const BORDER = "rgba(127,90,240,0.25)";
const MUTED = "#94a3b8";
const ERROR_COLOR = "#ef4444";

const LoginModal = ({ isOpen, onSuccess, onClose }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    // Small delay for UX feel
    await new Promise((r) => setTimeout(r, 600));
    const ok = login(email, password);
    setLoading(false);
    if (!ok) {
      setError("Invalid email or password.");
      return;
    }
    onSuccess();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "24px",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            style={{
              background: CARD_BG,
              border: `1.5px solid ${BORDER}`,
              borderRadius: "16px",
              padding: "40px 36px",
              width: "100%",
              maxWidth: "420px",
              boxShadow: `0 0 60px ${ACCENT}25`,
              position: "relative",
            }}
          >
            {/* Glow orb */}
            <div style={{ position: "absolute", top: "-60px", left: "50%", transform: "translateX(-50%)", width: "200px", height: "200px", background: `radial-gradient(circle, ${ACCENT}25, transparent 70%)`, pointerEvents: "none" }} />

            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "32px", position: "relative" }}>
              <img
                src="/Scrobits_logo_black.png"
                alt="Scrobits"
                style={{ height: "24px", filter: "invert(1)", opacity: 0.8, marginBottom: "16px", display: "block", margin: "0 auto 14px" }}
              />
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "20px", fontWeight: "900", color: "#fff", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "6px" }}>
                Access Required
              </div>
              <div style={{ fontSize: "13px", color: MUTED, lineHeight: "1.5" }}>
                Sign in to begin your HeroScan
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Email */}
              <div>
                <label style={{ display: "block", fontFamily: "'Orbitron', monospace", fontSize: "9px", color: MUTED, letterSpacing: "2px", textTransform: "uppercase", marginBottom: "8px" }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  placeholder="your@email.com"
                  autoComplete="email"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: "#1a1a2e", border: `1px solid ${error ? ERROR_COLOR + "60" : BORDER}`,
                    borderRadius: "8px", padding: "12px 14px",
                    color: "#fff", fontSize: "14px",
                    outline: "none", fontFamily: "inherit",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => e.target.style.borderColor = ACCENT}
                  onBlur={(e) => e.target.style.borderColor = error ? ERROR_COLOR + "60" : BORDER}
                />
              </div>

              {/* Password */}
              <div>
                <label style={{ display: "block", fontFamily: "'Orbitron', monospace", fontSize: "9px", color: MUTED, letterSpacing: "2px", textTransform: "uppercase", marginBottom: "8px" }}>
                  Password
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{
                      width: "100%", boxSizing: "border-box",
                      background: "#1a1a2e", border: `1px solid ${error ? ERROR_COLOR + "60" : BORDER}`,
                      borderRadius: "8px", padding: "12px 44px 12px 14px",
                      color: "#fff", fontSize: "14px",
                      outline: "none", fontFamily: "inherit",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => e.target.style.borderColor = ACCENT}
                    onBlur={(e) => e.target.style.borderColor = error ? ERROR_COLOR + "60" : BORDER}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    style={{
                      position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer",
                      color: MUTED, fontSize: "14px", padding: "4px",
                    }}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{ background: `${ERROR_COLOR}12`, border: `1px solid ${ERROR_COLOR}40`, borderRadius: "6px", padding: "10px 14px", fontSize: "12px", color: ERROR_COLOR }}
                >
                  {error}
                </motion.div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: "4px",
                  background: loading ? `${ACCENT}80` : `linear-gradient(135deg, ${ACCENT}, #6644cc)`,
                  color: "#fff", border: "none",
                  padding: "13px", borderRadius: "8px",
                  fontFamily: "'Orbitron', monospace", fontSize: "12px",
                  fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase",
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading ? "none" : `0 0 24px ${ACCENT}40`,
                  transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
              >
                {loading ? (
                  <>
                    <div style={{ width: "14px", height: "14px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                    Verifying...
                  </>
                ) : "Begin Your Scan →"}
              </button>
            </form>

            <div style={{ textAlign: "center", marginTop: "20px", fontSize: "11px", color: MUTED }}>
              Access restricted to authorised users only
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoginModal;
